import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ShieldCheck, User, Radio } from "lucide-react";
import { useAuth } from "../lib/auth";
import Logo from "./Logo";

const PUBLIC_NAV = [
  { to: "/", label: "Accueil" },
  { to: "/organisation", label: "Organisation" },
  { to: "/evenements", label: "Événements" },
  { to: "/medias", label: "Médias" },
  { to: "/actualites", label: "Actualités" },
  { to: "/web-tv", label: "Web TV" },
  { to: "/radio", label: "Radio" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll); return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_rgba(10,37,64,0.08)]" : "bg-white"
      }`}
    >
      {/* Top utility bar */}
      <div className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-2 flex items-center justify-between text-[0.72rem] tracking-[0.18em] uppercase font-semibold">
          <div className="flex items-center gap-2 text-gold">
            <span className="live-dot" />
            <span>Diaspora congolaise · Plaidoyer international</span>
          </div>
          <div className="hidden sm:flex items-center gap-5">
            <Link to="/web-tv" className="hover:text-gold transition-colors flex items-center gap-1.5" data-testid="topbar-webtv">
              Web TV
            </Link>
            <Link to="/radio" className="hover:text-gold transition-colors flex items-center gap-1.5" data-testid="topbar-radio">
              <Radio className="w-3 h-3" /> Radio
            </Link>
            {user?.role === "admin" ? (
              <Link to="/admin" className="hover:text-gold transition-colors flex items-center gap-1.5" data-testid="topbar-admin">
                <ShieldCheck className="w-3.5 h-3.5" /> Admin
              </Link>
            ) : (
              <Link to="/membres/connexion" className="hover:text-gold transition-colors flex items-center gap-1.5" data-testid="topbar-member-login">
                <User className="w-3.5 h-3.5" /> Espace membres
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="site-logo">
          <Logo className="w-11 h-11 shrink-0" />
          <div className="leading-tight">
            <div className="font-serif text-[1.05rem] text-ink tracking-tight">Front Uni pour la RDC</div>
            <div className="text-[0.68rem] tracking-[0.22em] uppercase font-semibold text-gold-dark">Plateforme officielle</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 text-[0.85rem] font-medium whitespace-nowrap">
          {PUBLIC_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative py-1 transition-colors hover:text-ink whitespace-nowrap ${
                  isActive ? "text-ink" : "text-foreground/70"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-[2px] bg-gold transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link to="/adhesion" className="text-sm font-semibold text-ink hover:text-gold-dark uppercase tracking-widest" data-testid="nav-cta-adhesion">
            Adhérer
          </Link>
          <Link to="/evenements" className="btn-primary text-sm whitespace-nowrap" data-testid="nav-cta-events">
            Voir les événements
          </Link>
        </div>

        <button
          aria-label="Menu"
          className="lg:hidden w-10 h-10 grid place-items-center"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6 text-ink" /> : <Menu className="w-6 h-6 text-ink" />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {PUBLIC_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `py-3 border-b border-border/60 text-[0.95rem] ${isActive ? "text-ink font-semibold" : "text-foreground/80"}`
                }
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-3 pt-3">
              {user?.role === "admin" ? (
                <Link to="/admin" className="btn-outline text-sm flex-1 justify-center" data-testid="mobile-admin">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              ) : (
                <Link to="/membres/connexion" className="btn-outline text-sm flex-1 justify-center" data-testid="mobile-member-login">
                  <User className="w-4 h-4" /> Espace membres
                </Link>
              )}
              <Link to="/evenements" className="btn-primary text-sm flex-1 justify-center">
                Événements
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
